import type { components } from '../types/generated';
import type { PhotoGalleryItem } from '@/components/PhotoGallery/PhotoGallery.types';

type GalleryImageDto = components['schemas']['GalleryImageDto'];

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export type GalleryCategory =
  | 'GALLERY_BODY'
  | 'GALLERY_INTERIOR'
  | 'GALLERY_ENGINE_COMPARTMENT'
  | 'GALLERY_UNDERCARRIAGE'
  | 'GALLERY_DIAGNOSTICS'
  | 'GALLERY_DOCUMENTATION'
  | 'GALLERY_OTHER'
  | 'DEFAULT_DOCUMENT';

export interface MappedGalleries {
  body: PhotoGalleryItem[];
  interior: PhotoGalleryItem[];
  engineCompartment: PhotoGalleryItem[];
  undercarriage: PhotoGalleryItem[];
  diagnostics: PhotoGalleryItem[];
  documentation: PhotoGalleryItem[];
  other: PhotoGalleryItem[];
  all: PhotoGalleryItem[];
  mainPhoto?: PhotoGalleryItem;
  mainPhotoAttachmentId?: string;
  mainPhotoUrl: string | undefined;
}

const buildImageUrl = (url: string | undefined): string => {
  if (!url) return '';
  // Jeśli URL jest już pełny (zaczyna się od http), zwróć go bez zmian
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // W przeciwnym razie dodaj prefix API
  return `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const mapGalleryImageToPhotoItem = (
  image: GalleryImageDto,
): PhotoGalleryItem => {
  // W siatce ładujemy miniaturkę (lekkie ~300x300), w lightboxie pełen rozmiar.
  // Backend wymaga autoryzacji obu endpointów — komponenty muszą używać
  // `AuthorizedImage`.
  const thumbnail = image.thumbnailUrl || image.fullSizeUrl || '';
  const fullSize = image.fullSizeUrl || image.thumbnailUrl || '';
  return {
    attachmentId: image.attachmentId,
    src: buildImageUrl(thumbnail),
    fullSrc: buildImageUrl(fullSize),
    width: 800, // Domyślne wartości, można dostosować
    height: 600,
    alt: image.displayName || 'Zdjęcie pojazdu',
    title: image.displayName,
  };
};

export const mapGalleriesToUI = (
  galleries: Record<string, GalleryImageDto[]> | null | undefined,
): MappedGalleries => {
  const emptyGallery: PhotoGalleryItem[] = [];

  if (!galleries) {
    return {
      body: emptyGallery,
      interior: emptyGallery,
      engineCompartment: emptyGallery,
      undercarriage: emptyGallery,
      diagnostics: emptyGallery,
      documentation: emptyGallery,
      other: emptyGallery,
      all: emptyGallery,
      mainPhoto: undefined,
      mainPhotoAttachmentId: undefined,
      mainPhotoUrl: undefined,
    };
  }

  const mappedGalleries: MappedGalleries = {
    body: [],
    interior: [],
    engineCompartment: [],
    undercarriage: [],
    diagnostics: [],
    documentation: [],
    other: [],
    all: [],
    mainPhoto: undefined,
    mainPhotoAttachmentId: undefined,
    mainPhotoUrl: undefined,
  };

  // Mapuj wszystkie galerie na podstawie klucza
  Object.entries(galleries).forEach(([key, images]) => {
    const mappedImages = images.map(mapGalleryImageToPhotoItem);
    const mainPhotoIndex = images.findIndex((image) => image.isMainPhoto);

    if (mainPhotoIndex >= 0 && !mappedGalleries.mainPhoto) {
      const mainPhoto = mappedImages[mainPhotoIndex];
      mappedGalleries.mainPhoto = mainPhoto;
      mappedGalleries.mainPhotoAttachmentId = mainPhoto.attachmentId;
      mappedGalleries.mainPhotoUrl = mainPhoto.src;
    }

    // Dodaj do odpowiedniej kategorii
    switch (key) {
      case 'GALLERY_BODY':
        mappedGalleries.body = mappedImages;
        break;
      case 'GALLERY_INTERIOR':
        mappedGalleries.interior = mappedImages;
        break;
      case 'GALLERY_ENGINE_COMPARTMENT':
        mappedGalleries.engineCompartment = mappedImages;
        break;
      case 'GALLERY_UNDERCARRIAGE':
        mappedGalleries.undercarriage = mappedImages;
        break;
      case 'GALLERY_DIAGNOSTICS':
        mappedGalleries.diagnostics = mappedImages;
        break;
      case 'GALLERY_DOCUMENTATION':
        mappedGalleries.documentation = mappedImages;
        break;
      case 'GALLERY_OTHER':
        mappedGalleries.other = mappedImages;
        break;
      default:
        // Jeśli nieznana kategoria, dodaj do "other"
        mappedGalleries.other = [...mappedGalleries.other, ...mappedImages];
        break;
    }

    // Dodaj wszystkie zdjęcia do galerii "all"
    mappedGalleries.all = [...mappedGalleries.all, ...mappedImages];
  });

  return mappedGalleries;
};
